/**
 * Product cache with TTL-aware localStorage storage.
 *
 * Key format: hl_pcache_{source}:{barcode}
 * TTL: 30 days for food (OFF), 90 days for cosmetics (OBF)
 */

const CACHE_PREFIX = 'hl_pcache_';
const TTL_FOOD_MS = 30 * 24 * 60 * 60 * 1000;      // 30 days
const TTL_COSMETICS_MS = 90 * 24 * 60 * 60 * 1000;  // 90 days

function cacheKey(source, barcode) {
    return `${CACHE_PREFIX}${source}:${barcode}`;
}

/**
 * Get a cached product. Returns null if not cached or expired.
 */
export function getCached(source, barcode) {
    const key = cacheKey(source, barcode);
    const raw = localStorage.getItem(key);
    if (!raw) return null;

    try {
        const entry = JSON.parse(raw);
        const ttl = source === 'obf' ? TTL_COSMETICS_MS : TTL_FOOD_MS;
        const age = Date.now() - entry.fetchedAt;

        if (age > ttl) {
            localStorage.removeItem(key);
            return null;
        }

        return entry;
    } catch {
        localStorage.removeItem(key);
        return null;
    }
}

/**
 * Store a product in cache.
 * @param {string} source - 'off' or 'obf'
 * @param {string} barcode 
 * @param {object} data - the normalized product data
 * @param {object} rawResponse - original API response (for debugging/re-parsing)
 */
export function setCache(source, barcode, data, rawResponse = null) {
    const key = cacheKey(source, barcode);
    const entry = {
        ...data,
        source,
        barcode,
        fetchedAt: Date.now(),
        rawResponse: rawResponse ? JSON.stringify(rawResponse).slice(0, 5000) : null, // truncate raw
    };
    try {
        localStorage.setItem(key, JSON.stringify(entry));
    } catch (e) {
        // localStorage quota exceeded — evict oldest entries
        evictOldEntries();
        try {
            localStorage.setItem(key, JSON.stringify(entry));
        } catch {
            // Still full — skip caching
        }
    }
}

/**
 * Mark a cached entry as stale (force refresh on next lookup).
 */
export function invalidateCache(source, barcode) {
    const key = cacheKey(source, barcode);
    localStorage.removeItem(key);
}

/**
 * Evict the oldest cached entries to free up space.
 */
function evictOldEntries() {
    const entries = [];
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(CACHE_PREFIX)) {
            try {
                const entry = JSON.parse(localStorage.getItem(key));
                entries.push({ key, fetchedAt: entry.fetchedAt || 0 });
            } catch {
                localStorage.removeItem(key);
            }
        }
    }

    // Sort by oldest first, remove the oldest 25%
    entries.sort((a, b) => a.fetchedAt - b.fetchedAt);
    const toRemove = Math.max(1, Math.floor(entries.length * 0.25));
    for (let i = 0; i < toRemove; i++) {
        localStorage.removeItem(entries[i].key);
    }
}

/**
 * Get cache stats for debugging.
 */
export function getCacheStats() {
    let count = 0;
    let totalSize = 0;
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(CACHE_PREFIX)) {
            count++;
            totalSize += (localStorage.getItem(key) || '').length;
        }
    }
    return { count, totalSizeKB: Math.round(totalSize / 1024) };
}
